import React, { useEffect, useMemo, useState } from "react";
import { Col } from "react-bootstrap";
import { BsFillStarFill } from "react-icons/bs";
import { FcGoogle } from "react-icons/fc";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function monthYear(yyyyMm) {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm || "");
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "";
}

/** Small seeded PRNG so the selection is stable for a day instead of reshuffling on every render. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pickReviews(all, count, seed) {
  const good = all.filter((r) => r.rating === 5 && r.text.length >= 80 && r.text.length <= 300);
  const pool = good.length >= count ? good : all.filter((r) => r.rating === 5);
  const random = mulberry32(seed === undefined ? Math.floor(Date.now() / 86400000) : seed);
  const shuffled = pool.slice();
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const tmp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = tmp;
  }
  const seenAuthors = new Set();
  const chosen = [];
  for (const review of shuffled) {
    if (seenAuthors.has(review.author)) continue;
    seenAuthors.add(review.author);
    chosen.push(review);
    if (chosen.length === count) break;
  }
  return chosen;
}

const Stars = () => (
  <span className="review-card__stars" role="img" aria-label="5 out of 5 stars">
    <BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill /><BsFillStarFill />
  </span>
);

/**
 * Guest reviews (Google, exported to src/data/reviews.json). The JSON is loaded on demand so it
 * stays out of the main bundle. Renders Bootstrap columns; wrap in a <Row>.
 */
const Reviews = ({ numToDisplay = 4, cols = { xs: 12, sm: 6, lg: 3 } }) => {
  const [all, setAll] = useState(null);

  useEffect(() => {
    let cancelled = false;
    import("../data/reviews.json")
      .then((mod) => {
        if (!cancelled) setAll(mod.default || mod);
      })
      .catch(() => {
        if (!cancelled) setAll([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chosen = useMemo(() => (all ? pickReviews(all, numToDisplay) : []), [all, numToDisplay]);

  if (!all) {
    return Array.from({ length: numToDisplay }).map((_, i) => (
      <Col key={i} {...cols} className="review-col">
        <div className="review-card review-card--skeleton" aria-hidden="true" />
      </Col>
    ));
  }

  return chosen.map((review) => (
    <Col key={review.id} {...cols} className="review-col">
      <article className="review-card">
        <div className="review-card__head">
          <FcGoogle aria-hidden="true" />
          <span>Google review</span>
          <Stars />
        </div>
        <blockquote className="review-card__text">“{review.text}”</blockquote>
        <footer className="review-card__meta">
          <span className="review-card__author">{review.author}</span>
          {review.date && <time dateTime={review.date}>{monthYear(review.date)}</time>}
        </footer>
        {review.url && (
          <a href={review.url} target="_blank" rel="noopener noreferrer" className="review-card__link">
            View on Google
          </a>
        )}
      </article>
    </Col>
  ));
};

export default Reviews;
